import app from 'flarum/forum/app';

// v0.6.5b — 不 import 'mithril' (webpack 解析不到), 改用全局 window.m 兜底
//   Flarum 2.0 用 mithril 全局变量, 不导出 npm 包
function safeRedraw() {
  try {
    if (typeof window !== 'undefined' && window.m && typeof window.m.redraw === 'function') {
      window.m.redraw();
    }
  } catch (e) {/* 静默兜底 */}
}

/**
 * LessonOptions — 课程分类 + 课程类型 配置前端 util (v0.6.5, 辉哥 9/26 SOP)
 *
 * 后端: GET /api/ziiven/scheduler/admin/lesson-options
 *   - 返 { categories: [...], types: [...] }, 合并 builtin + 自定义, 排除软删
 *   - 内置项 id 用 'builtin-' 前缀 (跟自定义 numeric id 不冲突)
 *
 * 用法:
 *   - 启动期 oninit 调 LessonOptions.load() 一次
 *   - UI 用 LessonOptions.categories() / LessonOptions.types() 拿同步数据
 *   - 数据缺失时返 builtin 兜底 (V 测 v0.6.5d 反馈 bjxy 端 LessonOptions load 失败时
 *     modal fallback "暂无课程分类", 用户看不到 builtin 项 — 现提供 builtin 兜底)
 *
 * 不缓存到全局 store: LessonOptions 数据少 (<20 项), 启动期 fetch 1 次够了,
 * admin 后台修改后下次刷新页面自动重新 fetch。
 *
 * v0.6.5b — fetch 完成后 m.redraw() 触发所有依赖 UI 重绘
 *   (修 v0.6.5a 留下的 bug: CardTemplateEditorModal 打开瞬间 fallback "暂无课程分类",
 *    因为 modal 渲染同步执行, 但 LessonOptions.load() 异步 fetch, 完成后没 redraw
 *    modal 永远显示 fallback。让 LessonOptions.load() 在 _loaded 变化后 redraw。)
 *
 * v0.6.5d — _data 初始值改成 builtin fallback (v0.6.5 bjxy 端 LessonOptions load 失败时
 *   modal fallback 显示空, 用户看不到 builtin 单板/双板/公园; 现在 builtin 始终显示
 *   API 成功后覆盖。Builtin 跟 php 端 LessonOptions::BUILTIN_CATEGORIES / BUILTIN_TYPES 同步)
 */

// v0.6.5d — builtin 兜底数据 (跟 src/Helpers/LessonOptions.php::BUILTIN_CATEGORIES / BUILTIN_TYPES 同步)
//   API 加载失败时也能渲染 builtin 项 (单板/双板/公园/私教/小班/体验)
//   字段 schema 跟 API 返的一致 (id/name/enabled/sort_order/deleted_at/is_builtin)
const BUILTIN_FALLBACK = {
  categories: [{
    id: 'builtin-snowboard',
    name: '单板',
    enabled: true,
    sort_order: 0,
    deleted_at: null,
    is_builtin: true
  }, {
    id: 'builtin-ski',
    name: '双板',
    enabled: true,
    sort_order: 1,
    deleted_at: null,
    is_builtin: true
  }, {
    id: 'builtin-park',
    name: '公园',
    enabled: true,
    sort_order: 2,
    deleted_at: null,
    is_builtin: true
  }],
  types: [{
    id: 'builtin-private',
    name: '私教',
    enabled: true,
    sort_order: 0,
    deleted_at: null,
    is_builtin: true
  }, {
    id: 'builtin-group',
    name: '小班',
    enabled: true,
    sort_order: 1,
    deleted_at: null,
    is_builtin: true
  }, {
    id: 'builtin-trial',
    name: '体验',
    enabled: true,
    sort_order: 2,
    deleted_at: null,
    is_builtin: true
  }]
};
const LessonOptions = {
  // v0.6.5d — _data 初始 builtin fallback (API 加载前/失败时都用)
  _data: {
    categories: BUILTIN_FALLBACK.categories.slice(),
    types: BUILTIN_FALLBACK.types.slice()
  },
  _loaded: false,
  _loading: null,
  async load(force) {
    if (force === void 0) {
      force = false;
    }
    if (this._loaded && !force) return this._data;
    if (this._loading) return this._loading;
    this._loading = (async () => {
      try {
        // v0.6.5c — 回滚到 app.request (辉哥 9/26 13:01 反馈 + V 测 FAIL 报告首选)
        //   v0.6.5b 改用 fetch API 仍然 403 (V 测独立验证 app.request + fetch + 各种 header 全 403),
        //   根因不在 fetch vs XHR, 而是 mithril 1.x 在某些场景不带 session cookie。
        //   改回 app.request (Flarum 原生, 自动带 csrf + session + 处理 401/403 alerts).
        //   v0.6.5b 留的 safeRedraw() 保留 (修 v0.6.5a fallback 卡死 bug, 跟 fetch 改动正交)。
        // v0.6.5d — errorHandler 静默处理: LessonOptions.load 失败时不弹 'permission_denied' alert
        //   (辉哥 14:11 反馈 bjxy 端 modal 底部 '您没有权限进行此操作' 是 app.request 默认错误处理弹的)
        //   v0.6.5d builtin fallback 兜底 (见 BUILTIN_FALLBACK), modal 仍能用 builtin 单板/双板/公园
        const r = await app.request({
          method: 'GET',
          url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/admin/lesson-options',
          // 静默错误处理 — 不弹 alert (返 undefined 走 default 不显示 alert 路径)
          //   v0.6.5d 修 E10: bjxy 端 LessonOptions 失败时 modal 底部不再显示 'permission_denied'
          errorHandler: e => {
            console.warn('[LessonOptions] silent error (modal uses builtin fallback):', e.status, e.message);
            // 不返回 false → 不走 default handler → 不弹 alert
          }
        });
        this._data = {
          categories: r && r.categories || [],
          types: r && r.types || []
        };
        this._loaded = true;
      } catch (e) {
        console.error('[LessonOptions] load failed', e && (e.message || e.toString()));
        this._data = {
          categories: [],
          types: []
        };
      }
      // v0.6.5b 留的 redraw, 修 v0.6.5a fallback 卡死 bug (modal 渲染时机问题)
      safeRedraw();
      return this._data;
    })();
    const result = await this._loading;
    this._loading = null;
    return result;
  },
  categories() {
    return this._data.categories || [];
  },
  types() {
    return this._data.types || [];
  },
  // UI 用: 过滤掉已禁用的 (enabled=false 不展示给用户选)
  typesEnabled() {
    return this.types().filter(t => t.enabled !== false);
  },
  // 拿单条 (按 id) — modal 渲染预填回显时用
  findCategory(id) {
    return this.categories().find(c => c.id === id) || null;
  },
  findType(id) {
    return this.types().find(t => t.id === id) || null;
  },
  // 拿展示 label (找不到返 id, modal fallback 用)
  labelFor(kind, id) {
    if (!id) return '';
    const item = kind === 'category' ? this.findCategory(id) : this.findType(id);
    return item && item.name || id;
  }
};
export default LessonOptions;
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/LessonOptions', LessonOptions);